// Curated fun, easy, friendly prompt starters shown as chips in the Workspace.
// Grouped by `kind` and further by `tool`, so the UI can filter dynamically.
// Keep prompts short (~4-12 words) and inviting — they should feel like a nudge,
// not a template.

export const SUGGESTED_PROMPTS = {
  text: {
    ideas: [
      "10 fun weekend hobby ideas for someone bored on a rainy Saturday",
      "Silly nickname ideas for my golden retriever",
      "3 birthday-party themes that don't require Pinterest",
      "Podcast topics for a first-time solo host",
      "Wild business ideas a 12-year-old could actually run",
      "20 side-hustles I can start with $50 and a phone",
      "5 conversation starters for someone who hates small talk",
      "Rainy-day activities for kids that don't involve screens",
      "Book recommendations for someone who just finished Dune",
      "Recipes for a lazy Sunday and only 4 ingredients",
    ],
    blog: [
      "A cozy blog post about my grandmother's coffee ritual",
      "How I finally stopped hitting snooze in the morning",
      "5 tiny apartment upgrades under $50",
      "Why I quit hustle culture (in 800 words)",
      "A love letter to my first bike",
      "The 3 books that quietly rewired my brain",
      "How I learned to say no without apologizing",
      "A day in the life of a night-shift baker",
      "Everything I wish I knew before starting a garden",
      "Why boredom is a superpower nobody talks about",
    ],
    social: [
      "A wholesome Instagram caption for a rainy dog walk",
      "A snappy tweet about Sunday-scaries",
      "Threads post: my favorite productivity myth to bust",
      "TikTok hook for a 3-ingredient dinner",
      "LinkedIn hot take on remote work culture",
      "IG caption for a golden-hour picnic photo",
      "A tweet thread about the tiny wins of the week",
      "Instagram Reel caption for a bookstore haul",
    ],
    email: [
      "Warm welcome email for a book club",
      "Casual product-launch email that doesn't sound salesy",
      "A short thank-you note to a first customer",
      "Re-engagement email for lapsed newsletter subscribers",
      "Cold outreach that feels human, not spammy",
      "A cheerful appointment-reminder for a hair salon",
    ],
    seo: [
      "SEO title + meta for a bakery that specializes in sourdough",
      "Keyword ideas for a beginner yoga blog",
      "H1s for a personal-finance article aimed at Gen Z",
      "Meta description for a Portland vegan restaurant",
      "Ten long-tail keywords for a birdwatching hobby site",
    ],
    scripts: [
      "60-second explainer script for a local coffee shop",
      "Voice-over script for a 'day-in-my-life' reel",
      "Podcast intro for a comedy show about houseplants",
      "30s YouTube pre-roll ad for a language app",
      "TikTok hook + payoff for a sneaker collector",
    ],
  },
  image: {
    default: [
      "A cozy reading nook lit by golden-hour sun, editorial photo",
      "Cute otter wearing tiny round glasses, studio lighting",
      "Retro 90s minivan on a desert highway, film grain",
      "Watercolor mountain range at dawn, calm colors",
      "A pastel city skyline made from stacked books",
      "A brutalist library floating in outer space, cinematic",
      "A tea ceremony on a moon of Saturn, editorial photo",
      "A fox chef in a Michelin-star kitchen, hyperrealistic",
      "A vintage 1940s poster for a Mars vacation",
      "A cyberpunk night-market ramen stall, neon reflections",
      "A snow globe containing a tiny bustling Tokyo",
      "A jazz club underwater with kelp curtains",
      "A magical bookstore where the aisles rearrange themselves",
    ],
  },
  video: {
    default: [
      "A hummingbird sipping from a red flower, slow-motion",
      "Espresso pour in extreme close-up, warm light",
      "A paper airplane gliding through a sunlit hallway",
      "Waves lapping onto a black-sand beach at sunset",
      "A neon-lit rainy Tokyo alley at 3am, cinematic",
      "A time-lapse of a city skyline turning to night",
      "A dandelion clock catching golden hour, macro",
      "Cinematic drone shot flying over a redwood forest",
      "A single droplet of ink dispersing in still water",
      "A vintage record spinning under warm tungsten lamps",
    ],
  },
  audio: {
    default: [
      "A calm 30-second intro for my meditation podcast",
      "An upbeat welcome greeting for my Etsy shop's video",
      "A soothing 20-second bedtime affirmation for kids",
      "A snappy voiceover for a 3-ingredient recipe reel",
      "A cinematic movie-trailer narration for a startup demo",
      "A warm audiobook opening for a mystery novel",
      "A late-night radio DJ intro for a jazz show",
      "A punchy 15-second ad tag for a fitness app",
    ],
  },
};

/** Return 4-6 curated prompts for the current (kind, tool) selection. */
export function getSuggestedPrompts(kind, tool) {
  const byKind = SUGGESTED_PROMPTS[kind] || {};
  const list = byKind[tool] || byKind.default || byKind.ideas || [];
  return list.slice(0, 6);
}

/** Return the full pool of prompts for the current (kind, tool) — used for the
 *  endless rotating placeholder in the prompt textarea. */
export function getPromptPool(kind, tool) {
  const byKind = SUGGESTED_PROMPTS[kind] || {};
  const primary = byKind[tool] || byKind.default || byKind.ideas || [];
  // Fold in a couple of prompts from the other tools in the same kind so the
  // rotation still feels varied when a single tool has a small pool.
  const extras = Object.entries(byKind)
    .filter(([t]) => t !== tool)
    .flatMap(([, arr]) => arr.slice(0, 2));
  const merged = [...primary, ...extras];
  // Dedupe + shuffle deterministically so the sequence changes on kind/tool switch.
  const seen = new Set();
  const uniq = merged.filter((p) => (seen.has(p) ? false : (seen.add(p), true)));
  const seed = (kind + "|" + tool).length;
  return uniq.map((p, i) => ({ p, r: ((i + seed) * 9301 + 49297) % 233280 }))
    .sort((a, b) => a.r - b.r)
    .map((x) => x.p);
}
